/**
 * Auth Store (Zustand)
 *
 * Lightweight auth state for components that need quick access to auth info.
 * Syncs with AuthContext automatically.
 * Uses persist middleware to survive page reloads.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface Role {
  code: string;
  name: string;
  description: string | null;
}

interface Account {
  id: string;
  telegramId: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  hasPassword: boolean;
  roles: Role[];
  permissions: string[];
  canAccessAdmin: boolean;
}

interface AuthState {
  // State
  isAuthenticated: boolean;
  account: Account | null;
  isMiniApp: boolean;

  // Actions
  setAuth: (account: Account | null, isMiniApp: boolean) => void;
  clearAuth: () => void;

  // Selectors (can be used directly)
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasRole: (roleCode: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      account: null,
      isMiniApp: false,

      setAuth: (account, isMiniApp) =>
        set({
          isAuthenticated: !!account,
          account,
          isMiniApp,
        }),

      clearAuth: () =>
        set({
          isAuthenticated: false,
          account: null,
          isMiniApp: false,
        }),

      hasPermission: (permission) => {
        const { account } = get();
        return account?.permissions.includes(permission) ?? false;
      },

      hasAnyPermission: (permissions) => {
        const { account } = get();
        if (!account) return false;
        return permissions.some((p) => account.permissions.includes(p));
      },

      hasRole: (roleCode) => {
        const { account } = get();
        return account?.roles.some((r) => r.code === roleCode) ?? false;
      },
    }),
    {
      name: "one-church:auth",
      storage: createJSONStorage(() => sessionStorage),
      // Only persist essential data, not functions
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        account: state.account,
        isMiniApp: state.isMiniApp,
      }),
    }
  )
);
